import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Newspaper, FileText, Lock, Mail, Info, MessageCircle, Heart, ExternalLink, Calculator } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useAppVersion } from '../../hooks/useAppVersion';
import { PageLayout } from '../../components/layout/PageLayout';
import { useTranslation } from '../../hooks/useTranslation';

export function OtherChoice() {
  const { user } = useAuth();
  const appVersion = useAppVersion();
  const { t, getPathForLang } = useTranslation();

  return (
    <PageLayout disableAds={true}>
      <div className="container mx-auto px-4 py-6 pb-8 md:pb-8">
        {/* Section principale */}
        <div className="space-y-6 max-w-lg mx-auto">
          {/* Section Nous soutenir - Mise en avant maximale */}
          <div className="mb-6">
            <a
              href="https://www.paypal.com/donate/?hosted_button_id=D95N2F3624FKQ"
              target="_blank"
              rel="noopener noreferrer"
              className="block p-5 bg-gradient-to-r from-blue-100 to-pink-100 rounded-2xl shadow-lg hover:shadow-xl transition-all border-2 border-pink-200 transform hover:scale-[1.01] relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 bg-pink-200 text-xs font-bold py-1 px-3 rounded-bl-lg text-pink-700">
                {t('other_choice.support_us.button')}
              </div>
              <div className="flex items-center mt-2">
                <div className="bg-pink-100 p-4 rounded-full shadow-inner border border-pink-200">
                  <Heart className="w-8 h-8 text-pink-600" />
                </div>
                <div className="ml-4">
                  <h2 className="font-bold text-lg text-gray-900">{t('other_choice.support_us.title')}</h2>
                  <p className="text-sm text-gray-700">{t('other_choice.support_us.description')}</p>
                  <div className="mt-2 inline-flex items-center text-xs font-medium text-pink-600">
                    {t('other_choice.support_us.donate')} <ExternalLink className="w-3 h-3 ml-1" />
                  </div>
                </div>
              </div>
            </a>
          </div>

          {/* Fonctionnalités principales */}
          <div>
            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 px-2">{t('other_choice.sections.features')}</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <Link
                to={getPathForLang('/actualites')}
                className="flex items-center p-4 border-b border-gray-100 hover:bg-blue-50 transition-colors"
              >
                <div className="bg-blue-100 p-3 rounded-full">
                  <Newspaper className="w-5 h-5 text-blue-600" />
                </div>
                <div className="ml-3">
                  <h2 className="font-semibold text-gray-900">{t('other_choice.features.news.title')}</h2>
                  <p className="text-xs text-gray-600">{t('other_choice.features.news.description')}</p>
                </div>
              </Link>

              {user ? (
                <Link
                  to={getPathForLang('/friend-codes')}
                  className="flex items-center p-4 border-b border-gray-100 hover:bg-blue-50 transition-colors"
                >
                  <div className="bg-indigo-100 p-3 rounded-full">
                    <Users className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="ml-3">
                    <h2 className="font-semibold text-gray-900">{t('other_choice.features.friend_codes.title')}</h2>
                    <p className="text-xs text-gray-600">{t('other_choice.features.friend_codes.description')}</p>
                  </div>
                </Link>
              ) : (
                <Link
                  to={getPathForLang('/login')}
                  className="flex items-center p-4 border-b border-gray-100 hover:bg-blue-50 transition-colors"
                >
                  <div className="bg-indigo-100 p-3 rounded-full">
                    <Users className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="ml-3">
                    <h2 className="font-semibold text-gray-900">{t('other_choice.features.friend_codes.title')}</h2>
                    <p className="text-xs text-gray-600">{t('other_choice.features.friend_codes.login_required')}</p>
                  </div>
                </Link>
              )}

              <Link
                to={getPathForLang('/messages')}
                className="flex items-center p-4 border-b border-gray-100 hover:bg-blue-50 transition-colors"
              >
                <div className="bg-purple-100 p-3 rounded-full">
                  <MessageCircle className="w-5 h-5 text-purple-600" />
                </div>
                <div className="ml-3">
                  <h2 className="font-semibold text-gray-900">{t('other_choice.features.messages.title')}</h2>
                  <p className="text-xs text-gray-600">{t('other_choice.features.messages.description')}</p>
                </div>
              </Link>

              <Link
                to={getPathForLang('/pokemon-luck')}
                className="flex items-center p-4 hover:bg-blue-50 transition-colors"
              >
                <div className="bg-yellow-100 p-3 rounded-full">
                  <Calculator className="w-5 h-5 text-yellow-600" />
                </div>
                <div className="ml-3">
                  <h2 className="font-semibold text-gray-900">{t('other_choice.features.luck_calculator.title')}</h2>
                  <p className="text-xs text-gray-600">{t('other_choice.features.luck_calculator.description')}</p>
                </div>
              </Link>
            </div>
          </div>

          {/* À propos & Contact */}
          <div>
            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 px-2">{t('other_choice.sections.information')}</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <Link
                to={getPathForLang('/about')}
                className="flex items-center p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors"
              >
                <div className="bg-green-100 p-3 rounded-full">
                  <Info className="w-5 h-5 text-green-600" />
                </div>
                <div className="ml-3">
                  <h2 className="font-semibold text-gray-900">{t('other_choice.information.about.title')}</h2>
                  <p className="text-xs text-gray-600">{t('other_choice.information.about.description')}</p>
                </div>
              </Link>

              <Link
                to={getPathForLang('/contact')}
                className="flex items-center p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="bg-teal-100 p-3 rounded-full">
                  <Mail className="w-5 h-5 text-teal-600" />
                </div>
                <div className="ml-3">
                  <h2 className="font-semibold text-gray-900">{t('other_choice.information.contact.title')}</h2>
                  <p className="text-xs text-gray-600">{t('other_choice.information.contact.description')}</p>
                </div>
              </Link>
            </div>
          </div>

          {/* Légal */}
          <div>
            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 px-2">{t('other_choice.sections.legal')}</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <Link
                to={getPathForLang('/mentions-legales')}
                className="flex items-center p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors"
              >
                <div className="bg-gray-100 p-3 rounded-full">
                  <FileText className="w-5 h-5 text-gray-600" />
                </div>
                <div className="ml-3">
                  <h2 className="font-semibold text-gray-900">{t('other_choice.legal.terms.title')}</h2>
                  <p className="text-xs text-gray-600">{t('other_choice.legal.terms.description')}</p>
                </div>
              </Link>

              <Link
                to={getPathForLang('/confidentialite')}
                className="flex items-center p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="bg-gray-100 p-3 rounded-full">
                  <Lock className="w-5 h-5 text-gray-600" />
                </div>
                <div className="ml-3">
                  <h2 className="font-semibold text-gray-900">{t('other_choice.legal.privacy.title')}</h2>
                  <p className="text-xs text-gray-600">{t('other_choice.legal.privacy.description')}</p>
                </div>
              </Link>
            </div>
          </div>

          {/* Version du site */}
          <div className="text-center text-xs text-gray-400 pt-2">
            {t('other_choice.version')}{appVersion}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}